import React, { useState } from 'react';
import { X, Lock, User, Sparkles, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { authService } from '../../services/api';

export default function AuthModal({
  isOpen,
  onClose,
  onLoginSuccess,
}) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await authService.login(username, password);
      setIsLoading(false);
      setShowSuccess(true);
      setTimeout(() => {
        onLoginSuccess();
        onClose();
        setShowSuccess(false);
      }, 1000);
    } catch (err) {
      setIsLoading(false);
      setError('Login failed. Please check credentials.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-brand-green-dark/60 backdrop-blur-sm"
      />

      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 15 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 15 }}
        className="bg-white rounded-2xl border border-gray-100 shadow-2xl max-w-md w-full overflow-hidden relative z-10 p-6 md:p-8"
        id="auth-modal-card"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-brand-green-dark transition-colors cursor-pointer p-1 rounded-full hover:bg-gray-50"
          id="btn-close-auth"
        >
          <X className="w-5 h-5" />
        </button>

        <AnimatePresence mode="wait">
          {showSuccess ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-8 flex flex-col items-center gap-3"
            >
              <div className="w-14 h-14 rounded-full bg-brand-green-light/10 text-brand-green-dark border-2 border-brand-green-light flex items-center justify-center mb-1">
                <Check className="w-6 h-6 text-brand-green-medium" />
              </div>
              <h3 className="font-serif text-xl text-brand-green-dark font-semibold">
                Access Granted
              </h3>
              <p className="text-xs text-gray-400 font-light">
                Welcome back, Admin. Loading your editing portal...
              </p>
            </motion.div>
          ) : (
            <motion.div key="form">
              <div className="flex flex-col gap-2 mb-6">
                <span className="text-[10px] tracking-[0.2em] text-brand-gold uppercase font-bold flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" />
                  Practitioner Portal
                </span>
                <h3 className="font-serif text-2xl text-brand-green-dark font-medium">
                  Administrative Login
                </h3>
                <p className="text-xs text-gray-400 font-light leading-relaxed">
                  Log in to create new articles, edit existing drafts, and manage live content.
                </p>
              </div>

              {error && (
                <div className="mb-4 text-xs text-red-500 bg-red-50 p-2 rounded border border-red-100 text-center">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="flex flex-col gap-4" autoComplete="off">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="auth-username" className="text-xs font-semibold uppercase tracking-wide text-gray-400 flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-brand-gold" />
                    Username
                  </label>
                  <input
                    id="auth-username"
                    name="username-prevent-autofill"
                    type="text"
                    required
                    autoComplete="off"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="admin"
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-brand-gold focus:outline-none bg-brand-cream-light/30"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="auth-password" className="text-xs font-semibold uppercase tracking-wide text-gray-400 flex items-center gap-1.5">
                    <Lock className="w-3.5 h-3.5 text-brand-gold" />
                    Security Password
                  </label>
                  <input
                    id="auth-password"
                    name="password-prevent-autofill"
                    type="password"
                    required
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-brand-gold focus:outline-none bg-brand-cream-light/30"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-brand-green-medium hover:bg-brand-green-dark text-white py-3 rounded-xl text-xs font-semibold uppercase tracking-wider transition-all mt-3 cursor-pointer shadow-sm border border-brand-green-dark/30 flex items-center justify-center"
                >
                  {isLoading ? 'Verifying Credentials...' : 'Verify & Enter'}
                </button>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
